import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import PageBlock from "../../../components/PageBlock";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import DashboardCard from "../../../components/DashboardCard";
import {
    DashboardStatisticInterface,
    DashboardStatisticResponse
} from "../../../types/Responses/DashboardStatisticResponse";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import DashboardCardSkeleton from "../../../components/Skeletons/DashboardCardSkeleton";
import {IconName} from "@fortawesome/free-regular-svg-icons";
import {
    DashboardDepositChartsResponseInterface,
    DashboardDepositObjectInterface, DashboardSalesResponseInterface
} from "../../../types/Responses/DashboardDepositChartsResponse";
import {
    DashboardWithdrawChartsResponseInterface,
    DashboardWithdrawObjectInterface
} from "../../../types/Responses/DashboardWithdrawChartsResponse";
import DashboardCharts from "../../../components/Charts/DashboardCharts";
import DatePickerMenu from "../../../components/DatePickerMenu";
import {
    DashboardLinearChartsResponseInterface,
    DashboardLinearObjectInterface
} from "../../../types/Responses/DashboardLinearChartsResponse";
import DashboardLinearCharts from "../../../components/Charts/DashboardLinearCharts";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {useTranslation} from "react-i18next";
import {useUserContext} from "../../../types/contexts/UserContext";
import {setTitle} from "../../../hooks/setTitle";
import {UserShortInfo} from "../../../types/Responses/PayoutLimitsResponse";
import DropdownInput from "../../../components/DropdownInput";
import FormSkeleton from "../../../components/Skeletons/FormSkeleton";
import {Card, CardBody, Typography} from "@material-tailwind/react";
import BarChart from "../../../components/Charts/BarChart";
import LinearChart from "../../../components/Charts/LinearChart";
import LocationsChart from "../../../components/Charts/LocationsChart";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {setDepositLoading, setLinearLoading, setSalesLoading, setWithdrawLoading} from "../../../reducers/dashboard";
import {setSidebarTitle} from "../../../reducers/sidebar";

const BrokerReport = () => {
    const {t} = useTranslation();
    const toast = useToastContext();
    const user = useUserContext();
    const handleError = useErrorHandler();
    const dateFormat = 'YYYY-MM-DD';

    const dispatch = useDispatch();
    const loading = useSelector((state: RootState) => state.dashboard);
    const [brokers, setBrokers] = useState<UserShortInfo[]>()
    const [cards, setCards] = useState<DashboardStatisticInterface[]>();
    const [chartDateStart, setChartDateStart] = useState<string>(moment().format(dateFormat));
    const [chartDateEnd, setChartDateEnd] = useState<string>(moment().format(dateFormat));
    const [depositCharts, setDepositCharts] = useState<DashboardDepositObjectInterface>();
    const [withdrawCharts, setWithdrawCharts] = useState<DashboardWithdrawObjectInterface>();
    const [linearCharts, setLinearCharts] = useState<DashboardLinearObjectInterface>();
    const [salesCharts, setSalesCharts] = useState<DashboardSalesResponseInterface>();
    const [filterBroker, setFilterBroker] = useState<number>(0);

    const setStartDate = useCallback((v: string) => {
        setChartDateStart(v);
        if (moment(v).diff(moment(chartDateEnd)) > 0) {
            setChartDateEnd(v);
        }
    }, [setChartDateStart, setChartDateEnd, chartDateEnd]);


    const loadLinearCharts = useCallback((id: number, from: string, to: string) => {
        setDepositCharts({successful: [], declined: [], outdated: []});
        dispatch(setLinearLoading(true));
        API.getLinearChart(from, to, id).then((response: AxiosResponse) => {
            const chartsResponse = response.data as DashboardLinearChartsResponseInterface;
            setLinearCharts(chartsResponse.linear);
            dispatch(setLinearLoading(false));
        }).catch(e => {
            dispatch(setLinearLoading(false));
            ErrorHandler(e, `get${APILink.GET_LINEAR_CHART}`, toast, handleError)
        });
    }, [setDepositCharts, setLinearCharts, toast, handleError, dispatch, setLinearLoading]);

    const loadDepositCharts = useCallback((id: number, from: string, to: string) => {
        setDepositCharts({successful: [], declined: [], outdated: []});
        dispatch(setDepositLoading(true));
        API.getDepositChart(from, to, id).then((response: AxiosResponse) => {
            const depositChartsResponse = response.data as DashboardDepositChartsResponseInterface;
            setDepositCharts(depositChartsResponse.payment_methods);
            dispatch(setDepositLoading(false));
        }).catch(e => {
            ErrorHandler(e,`get${APILink.GET_DEPOSIT_CHART}`, toast, handleError);
            dispatch(setDepositLoading(true));
        });
    }, [setDepositCharts, handleError, toast, dispatch, setDepositLoading]);

    const loadWithdrawCharts = useCallback((id: number, from: string, to: string) => {
        setWithdrawCharts({successful: [], pending: [], declined: []});
        dispatch(setWithdrawLoading(true));
        API.getWithdrawChart(from, to, id).then((response: AxiosResponse) => {
            const withdrawChartsResponse = response.data as DashboardWithdrawChartsResponseInterface;
            setWithdrawCharts(withdrawChartsResponse.providers);
            dispatch(setWithdrawLoading(false));
        }).catch(e => {
            ErrorHandler(e,`get${APILink.GET_WITHDRAW_CHART}`, toast, handleError);
            dispatch(setWithdrawLoading(false));
        });
    }, [setWithdrawCharts, toast, handleError, dispatch, setWithdrawLoading]);

    const loadSalesCharts = useCallback((id:number, from: string, to: string) => {
        setSalesCharts({decline_reasons: [], device_types: [], approved_ratio: [], locations: []});
        dispatch(setSalesLoading(true));
        API.getSalesChart(from, to, id).then((response: AxiosResponse) => {
            const salesChartsResponse = response.data.sales as DashboardSalesResponseInterface;
            setSalesCharts(salesChartsResponse);
            dispatch(setSalesLoading(false));
        }).catch(e => {
            ErrorHandler(e,`get${APILink.GET_SALES_CHART}`, toast, handleError);
            dispatch(setSalesLoading(false));
        });
    }, [toast, handleError, dispatch, setSalesLoading]);

    const loadCharts = useCallback((id?: number, from?: string, to?: string) => {
        loadDepositCharts(id ?? filterBroker, from ?? chartDateStart, to ?? chartDateEnd);
        loadWithdrawCharts(id ?? filterBroker, from ?? chartDateStart, to ?? chartDateEnd);
        loadLinearCharts(id ?? filterBroker, from ?? chartDateStart, to ?? chartDateEnd);
        loadSalesCharts(id ?? filterBroker, from ?? chartDateStart, to ?? chartDateEnd);
    }, [chartDateStart, chartDateEnd, filterBroker, loadDepositCharts, loadWithdrawCharts, loadLinearCharts, loadSalesCharts]);

    const getBrokers = useCallback(() => {
        API.getBrokersList().then((response: AxiosResponse) => {
            setBrokers(response.data.data as UserShortInfo[]);
        }).catch(e => ErrorHandler(e,`get${APILink.GET_BROKERS_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const loadDashboardStatistic = useCallback((id: number) => {
        API.brokerDashboard(id).then((response: AxiosResponse) => {
            const cardsResponse = response.data.data as DashboardStatisticResponse;
            setCards(cardsResponse.cards);
        }).catch(e => ErrorHandler(e, `get${APILink.BROKER_DASHBOARD}`,toast, handleError));

        loadCharts(id, chartDateStart, chartDateEnd);
    }, [chartDateEnd, chartDateStart, loadCharts, handleError, toast]);

    const onUserChanged = useCallback((u: string | number) => {
        setFilterBroker(typeof u === "string" ? parseInt(u) : u);
        loadDashboardStatistic(typeof u === "string" ? parseInt(u) : u);
    }, [setFilterBroker, loadDashboardStatistic]);

    useEffect(() => {
        setTitle(t('admin.menu.dashboard'));
        dispatch(setSidebarTitle(t('admin.menu.dashboard')));
        if (user && user.user){
            checkEnabled2FA(user.user).then(() => {
                getBrokers();
            }).catch();
        }
    }, [t, getBrokers, user, dispatch]);

    const cardsSkeletonsUI = useMemo(() => {
        return <>
            <DashboardCardSkeleton/>
            <DashboardCardSkeleton/>
            <DashboardCardSkeleton/>
            <DashboardCardSkeleton/>
        </>;
    }, []);

    const cardsUI = useMemo(() => {
        if (!user || !user.user || !cards)
            return cardsSkeletonsUI;
        return cards.map((card) =>
            <DashboardCard key={card.name} color={card.color} name={t(card.name)}
                           count={card.count + (card.name.toLowerCase().split('deposits').length > 1 ? ' ' + user.user.currency : '')}
                           icon={<FontAwesomeIcon size="3x" icon={card.icon as IconName}
                                                  className="float-left mt-1 w-8 h-8 md:w-12 md:h-12"/>}/>)
    }, [cards, cardsSkeletonsUI, user, t]);

    const datePickerMenuUI = useMemo(() => {
        return <DatePickerMenu dateFormat={dateFormat} startDate={chartDateStart} endDate={chartDateEnd}
                               setEnd={setChartDateEnd} setStart={setStartDate} onChanged={(f, t) => loadCharts(filterBroker, f, t)}/>
    }, [chartDateEnd, chartDateStart, loadCharts, setStartDate, filterBroker]);

    const depositAndWithdrawChartsUI = useMemo(() => {
        if (!depositCharts || !withdrawCharts || !salesCharts)
            return <></>;

        return <DashboardCharts elements={[
            {
                title: t('admin.dashboard.deposits.successful'),
                tooltip: t('dashboard.tooltips.successful_deposits'),
                data: {
                    labels: depositCharts.successful.map(item => item.name),
                    series: depositCharts.successful.map(item => item.count),
                    isLoading: loading.deposit
                }
            },
            {
                title: t('admin.dashboard.deposits.declined'),
                tooltip: t('dashboard.tooltips.declined_deposits'),
                data: {
                    labels: depositCharts.declined.map(item => item.name),
                    series: depositCharts.declined.map(item => item.count),
                    isLoading: loading.deposit
                }
            },
            {
                title: t('admin.dashboard.deposits.outdated'),
                tooltip: t('dashboard.tooltips.outdated_deposits'),
                data: {
                    labels: depositCharts.outdated.map(item => item.name),
                    series: depositCharts.outdated.map(item => item.count),
                    isLoading: loading.deposit
                }
            },
            {
                title: t('admin.dashboard.withdraws.successful'),
                tooltip: t('dashboard.tooltips.successful_withdrawals'),
                data: {
                    labels: withdrawCharts.successful.map(item => item.name),
                    series: withdrawCharts.successful.map(item => item.count),
                    isLoading: loading.withdraws
                }
            },
            {
                title: t('admin.dashboard.withdraws.declined'),
                tooltip: t('dashboard.tooltips.declined_withdrawals'),
                data: {
                    labels: withdrawCharts.declined.map(item => item.name),
                    series: withdrawCharts.declined.map(item => item.count),
                    isLoading: loading.withdraws
                }
            },
            {
                title: t('admin.charts.device_types'),
                tooltip: t('dashboard.tooltips.device_types'),
                data: {
                    labels: salesCharts.device_types.map(item => t(item.name)),
                    series: salesCharts.device_types.map(item => item.count),
                    isLoading: loading.sales
                }
            }
        ]}/>
    }, [depositCharts, withdrawCharts, salesCharts, t, loading]);

    const linearChartsUI = useMemo(() => {
        if (!linearCharts)
            return <></>;

        return <DashboardLinearCharts elements={[
            {
                title: t('admin.dashboard.deposits.amount'),
                tooltip: t('dashboard.tooltips.successful_deposits_amount'),
                data: {
                    labels: linearCharts.amount.map(item => item.title),
                    series: [{name: t('Amount'), data: linearCharts.amount.map(item => item.amount)}],
                    isLoading: loading.linear
                }
            },
            {
                title: t('admin.dashboard.deposits.count'),
                tooltip: t('dashboard.tooltips.successful_deposits_count'),
                data: {
                    labels: linearCharts.count.map(item => item.title),
                    series: [{name: t('Count'), data: linearCharts.count.map(item => item.count)}],
                    isLoading: loading.linear
                }
            }
        ]}/>
    }, [linearCharts, t, loading]);

    const salesChartsUI = useMemo(() => {
        if (!salesCharts)
            return <></>

        return <div className="w-full">
            <div className="grid md:grid-cols-2 gap-4 mt-12 mb-4">
                <Card key={t('admin.charts.decline_reasons')}
                      className="bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="w-full px-2 block h-96" id='device_types'>
                        <Typography variant="h6"
                                    className="mb-6 text-center">{t('admin.charts.decline_reasons')}</Typography>
                        <BarChart
                            isLoading={loading.sales}
                            series={{
                                name: t('Count'),
                                data: salesCharts.decline_reasons.map(item => item.count) ?? []
                            }}
                            labels={salesCharts.decline_reasons.map(item => t(item.name)) ?? []}
                        />
                    </CardBody>
                </Card>
                <Card key={t('admin.charts.approved_ratio')}
                      className="bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="w-full px-2 block h-96" id='device_types'>
                        <Typography variant="h6"
                                    className="mb-6 text-center">{t('admin.charts.approved_ratio')}</Typography>
                        <LinearChart
                            isLoading={loading.sales}
                            series={[{name: t('Percents'), data: salesCharts.approved_ratio.map(item => item.count)}]}
                            labels={salesCharts.approved_ratio.map(item => item.title)}/>
                    </CardBody>
                </Card>
            </div>
            <div className="w-full mt-12 mb-4">
                <Card key={t('admin.charts.locations')} className="bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="w-full px-2 block min-h-[24rem] h-auto lg:h-96" id='locations_chart'>
                        <Typography variant="h6"
                                    className="mb-6 text-center">{t('admin.charts.locations')}</Typography>
                        <LocationsChart
                            isLoading={loading.sales}
                            series={[
                            ['Country', t('admin.charts.locations_table.transactions'), t('admin.charts.locations_table.percent_with_prefix')],
                            ...salesCharts.locations.map(item => [item.name, item.count, parseFloat(item.percent)])
                        ]} extra={salesCharts.locations.map(item => [item.percent])} />
                    </CardBody>
                </Card>
            </div>
        </div>
    }, [salesCharts, t, loading]);

    const brokerUI = useMemo(() => {
        if (!brokers)
            return <FormSkeleton/>;

        return <div
            className="mt-4 px-4 py-2 max-w-md bg-clip-border bg-white rounded-xl text-gray-700 shadow-md dark:bg-blue-gray-900 dark:text-gray-500">
            <DropdownInput className="my-4" name={t('admin.virtual_terminal.table.broker')} list={brokers.map((u) => {
                return {name: u.name, id: parseInt(u.id)}
            })} selected={filterBroker} searchable={true} onChange={onUserChanged}/>
        </div>
    }, [brokers, filterBroker, onUserChanged, t]);

    const brokerInfoUI = useMemo(() => {
        if (!filterBroker)
            return <></>;

        return <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 mt-4">
                {cardsUI}
            </div>
            {datePickerMenuUI}
            {depositAndWithdrawChartsUI}
            {linearChartsUI}
            {salesChartsUI}
        </>
    }, [filterBroker, cardsUI, datePickerMenuUI, depositAndWithdrawChartsUI, linearChartsUI, salesChartsUI]);

    const statisticUI = useMemo(() => {
        return <>
            {brokerUI}
            {brokerInfoUI}
        </>
    }, [brokerUI, brokerInfoUI]);

    return (
        <PageBlock>
            {statisticUI}
        </PageBlock>
    );
}
export default memo(BrokerReport);
